import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Grid, SxProps, Typography} from '@mui/material';

import {DreamEmployerGraph} from '../culture/dashboard/components/DreamEmployer/DreamEmployerGraph/DreamEmployerGraph';
import {graphSerializer} from '../culture/dashboard/components/DreamEmployer/DreamEmployerGraph/graphSerializer';
import {isLegacyDreamEmployer} from '../culture/dashboard/components/DreamEmployer/DreamEmployerUtils/isLegacyDreamEmployer';

import {QuestionCards} from './QuestionCards';
import {bodyPdf, pdfH1} from './styles';
import {CultureAnalysisPdfContext} from '../../../contexts/CultureAnalysisPdfContext/CultureAnalysisPdfContext';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors, empionOrangeGradient} from '../../../layouts/theme/designSystem';

export const DreamEmployerComparisonFirstPage = () => {
  const {t} = useTranslation('pdfDomain');
  const currentLanguage = useContext(LanguageContext);
  const employeesLabel = t('shared.employees');

  const {data, loading} = useContext(CultureAnalysisPdfContext);

  if (loading || !data) {
    return null;
  }

  const legendIconStyles: SxProps = {
    width: '8px',
    height: '30px',
    borderRadius: 2,
  };

  return (
    <>
      <Grid container sx={{background: empionOrangeGradient.dark}} height={'280px'} p={5}>
        <Typography variant='h1' sx={pdfH1} mb={2}>
          {t('dreamEmployerComparison.title')}
        </Typography>
        <Typography variant='h1' sx={pdfH1} mb={2} color={orangeColors[70]} ml={1}>
          1/2
        </Typography>
        {isLegacyDreamEmployer(data.dreamEmployerResults) ? (
          <Typography sx={bodyPdf} mb={1}>
            {t('dreamEmployerComparison.textBlock')}
          </Typography>
        ) : (
          <>
            <Typography sx={bodyPdf} mb={1}>
              {t('dreamEmployerComparison.resultsDescription')}
            </Typography>
            <Typography sx={bodyPdf} mb={1}>
              <strong>{t('dreamEmployerComparison.example')}</strong>
              {t('dreamEmployerComparison.exampleDescription')}
            </Typography>
          </>
        )}
      </Grid>

      <Grid container display='flex' justifyContent='center' alignItems='center' mt={3}>
        {isLegacyDreamEmployer(data.dreamEmployerResults) && (
          <Grid
            container
            bgcolor={orangeColors[5]}
            p={2}
            borderRadius={'16px'}
            width={'340px'}
            border={`1px solid ${orangeColors[3]}`}
          >
            <Grid item container mb={1} flexWrap={'nowrap'}>
              <Grid item sx={legendIconStyles} bgcolor={greenColors[100]} />
              <Typography sx={bodyPdf} ml={1}>
                {t('dreamEmployerComparison.legend.employees')}
              </Typography>
            </Grid>
            <Grid item container flexWrap={'nowrap'}>
              <Grid item sx={legendIconStyles} bgcolor={orangeColors[100]} />
              <Typography sx={bodyPdf} ml={1}>
                {t('dreamEmployerComparison.legend.dreamEmployer')}
              </Typography>
            </Grid>
          </Grid>
        )}
        {!isLegacyDreamEmployer(data.dreamEmployerResults) ? (
          <Grid container justifyContent={'space-between'} alignItems={'center'} paddingX={2}>
            {data.dreamEmployerResults.questions.slice(0, 4).map((item, index) => (
              <Grid item key={index} maxWidth={'370px'} width={'100%'}>
                <DreamEmployerGraph
                  key={`${item.title[currentLanguage]}-${index}`}
                  title={item.title[currentLanguage]}
                  leftLabel={item.leftOption[currentLanguage]}
                  rightLabel={item.rightOption[currentLanguage]}
                  chartData={graphSerializer(item, employeesLabel)}
                  isPdf
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid item container px={5} mt={5}>
            <QuestionCards questions={data.dreamEmployerResults.questions.slice(0, 3)} removeLegend />
          </Grid>
        )}
      </Grid>
    </>
  );
};
